import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";

/*
 * Кадры, посещаемость, отпуска, заявки на расходы, двери и терминалы:
 * клиент из contracts/openapi.json (модули hr, attendance, leave, expense
 * из #59, access и presence из #56, панели дверей в агентах). Типы
 * сгенерированы, отказ это problem+json с кодом из errors.properties.
 */
type S = components["schemas"];
export type Employee = S["HrEmployee"];
export type Schedule = S["HrSchedule"];
export type Shift = S["AttendanceShift"];
export type ShiftEdit = S["AttendanceEditView"];
export type OvertimeDay = S["AttendanceOvertimeDay"];
export type Timesheet = S["AttendanceTimesheet"];
export type AttendanceSettings = S["AttendanceSettingsView"];
export type Kiosk = S["KioskDevice"];
export type KioskMark = S["KioskMark"];
export type KioskEmployee = S["KioskEmployee"];
export type Leave = S["LeaveView"];
export type LeaveKind = S["LeaveInput"]["kind"];
export type Holiday = S["HolidayView"];
export type Expense = S["ExpenseView"];
export type ExpenseInput = S["ExpenseInput"];
export type ExpenseCategory = ExpenseInput["category"];
export type CardNo = S["CardNoView"];
export type AccessDay = S["AccessDayView"];
export type AccessPass = S["AccessPassView"];
export type Agent = S["AgentView"];
export type Terminal = S["DeviceView"];
export type Workstation = S["WorkstationView"];
export type Session = S["SessionView"];
export type LineSpot = S["LineView"];

export const LEAVE_KINDS: readonly LeaveKind[] = ["ANNUAL", "UNPAID", "SICK"];
export const EXPENSE_CATEGORIES: readonly ExpenseCategory[] = ["OFFICE", "DELIVERY", "FUEL", "OTHER"];

const path = (id: string) => ({ params: { path: { id } } });
const date = (d: string) => ({ params: { query: { date: d } } });

export const hrApi = {
  employees: () => unwrap(api.GET("/api/v1/hr/employees")),
  create: (name: string, badge: string | null) => unwrap(api.POST("/api/v1/hr/employees", { body: { name, badge } })),
  rename: (id: string, name: string) => unwrap(api.PUT("/api/v1/hr/employees/{id}/name", { ...path(id), body: { name } })),
  badge: (id: string, badge: string | null) => unwrap(api.PUT("/api/v1/hr/employees/{id}/badge", { ...path(id), body: { badge } })),
  pin: (id: string, pin: string | null) => unwrap(api.PUT("/api/v1/hr/employees/{id}/pin", { ...path(id), body: { pin } })),
  schedule: (id: string, schedule: Schedule | null) => unwrap(api.PUT("/api/v1/hr/employees/{id}/schedule", { ...path(id), body: { schedule } })),
  archive: (id: string, archived: boolean) =>
    archived ? unwrap(api.POST("/api/v1/hr/employees/{id}/archive", path(id))) : unwrap(api.POST("/api/v1/hr/employees/{id}/restore", path(id))),

  cardNumbers: () => unwrap(api.GET("/api/v1/access/card-numbers")),
  setCardNumber: (employeeId: string, cardNo: string | null) =>
    unwrap(api.PUT("/api/v1/access/card-numbers/{employeeId}", { params: { path: { employeeId } }, body: { cardNo } })),
};

export const attendanceApi = {
  shifts: (from: string, to: string) => unwrap(api.GET("/api/v1/attendance/shifts", { params: { query: { from, to } } })),
  create: (employeeId: string, checkIn: string, checkOut: string | null) =>
    unwrap(api.POST("/api/v1/attendance/shifts", { body: { employeeId, checkIn, checkOut } })),
  checkIn: (id: number, at: string) => unwrap(api.PUT("/api/v1/attendance/shifts/{id}/check-in", { params: { path: { id } }, body: { at } })),
  checkOut: (id: number, at: string | null) => unwrap(api.PUT("/api/v1/attendance/shifts/{id}/check-out", { params: { path: { id } }, body: { at } })),
  remove: (id: number) => unwrap(api.DELETE("/api/v1/attendance/shifts/{id}", { params: { path: { id } } })),
  history: (id: number) => unwrap(api.GET("/api/v1/attendance/shifts/{id}/history", { params: { path: { id } } })),
  overtime: (employeeId: string, day: string) => unwrap(api.GET("/api/v1/attendance/overtime", { params: { query: { employeeId, date: day } } })),
  confirm: (employeeId: string, day: string) => unwrap(api.POST("/api/v1/attendance/overtime/confirm", { body: { employeeId, date: day } })),
  timesheet: (month: string | undefined) => unwrap(api.GET("/api/v1/attendance/timesheet", { params: { query: { month } } })),
  settings: () => unwrap(api.GET("/api/v1/attendance/settings")),
  saveSettings: (body: AttendanceSettings) => unwrap(api.PUT("/api/v1/attendance/settings", { body })),
  kiosks: () => unwrap(api.GET("/api/v1/attendance/kiosks")),
  createKiosk: (name: string) => unwrap(api.POST("/api/v1/attendance/kiosks", { body: { name } })),
  revokeKiosk: (id: string) => unwrap(api.POST("/api/v1/attendance/kiosks/{id}/revoke", path(id))),
};

/** Киоск ходит без сессии: токен устройства в заголовке Authorization. */
const bearer = (token: string) => ({ header: { Authorization: `Bearer ${token}` } });

export const kioskApi = {
  employees: (token: string) => unwrap(api.GET("/api/v1/kiosk/employees", { params: bearer(token) })),
  badge: (token: string, badge: string) => unwrap(api.POST("/api/v1/kiosk/badge", { params: bearer(token), body: { badge } })),
  pin: (token: string, employeeId: string, pin: string) => unwrap(api.POST("/api/v1/kiosk/pin", { params: bearer(token), body: { employeeId, pin } })),
};

export const leaveApi = {
  list: (employeeId: string) => unwrap(api.GET("/api/v1/leave", { params: { query: { employeeId } } })),
  request: (employeeId: string, kind: LeaveKind, from: string, to: string, note: string) =>
    unwrap(api.POST("/api/v1/leave", { body: { employeeId, kind, from, to, note } })),
  approve: (id: number) => unwrap(api.POST("/api/v1/leave/{id}/approve", { params: { path: { id } } })),
  refuse: (id: number, reason: string) => unwrap(api.POST("/api/v1/leave/{id}/refuse", { params: { path: { id } }, body: { reason } })),
  cancel: (id: number) => unwrap(api.POST("/api/v1/leave/{id}/cancel", { params: { path: { id } } })),
  holidays: () => unwrap(api.GET("/api/v1/leave/holidays")),
  addHoliday: (day: string, name: string) => unwrap(api.POST("/api/v1/leave/holidays", { body: { date: day, name } })),
  removeHoliday: (day: string) => unwrap(api.DELETE("/api/v1/leave/holidays/{date}", { params: { path: { date: day } } })),
};

export const expenseApi = {
  list: () => unwrap(api.GET("/api/v1/expenses")),
  create: (body: ExpenseInput) => unwrap(api.POST("/api/v1/expenses", { body })),
  approve: (id: number) => unwrap(api.POST("/api/v1/expenses/{id}/approve", { params: { path: { id } } })),
  refuse: (id: number, reason: string) => unwrap(api.POST("/api/v1/expenses/{id}/refuse", { params: { path: { id } }, body: { reason } })),
};

export const doorsApi = {
  days: (d: string) => unwrap(api.GET("/api/v1/access/days", date(d))),
  passes: (d: string) => unwrap(api.GET("/api/v1/access/passes", date(d))),
  /** Панели TVT у дверей живут за агентами: список агентов несёт их панели. */
  agents: () => unwrap(api.GET("/api/v1/agents")),
};

export const terminalsApi = {
  devices: () => unwrap(api.GET("/api/v1/presence/devices")),
  update: (id: string, enabled: boolean, name: string, workstationId: string | null) =>
    unwrap(api.PUT("/api/v1/presence/devices/{id}", { ...path(id), body: { enabled, name, workstationId } })),
  sync: (id: string) => unwrap(api.POST("/api/v1/presence/devices/{id}/sync", path(id))),
  workstations: () => unwrap(api.GET("/api/v1/presence/workstations")),
  sessions: (d: string) => unwrap(api.GET("/api/v1/presence/sessions", date(d))),
  line: () => unwrap(api.GET("/api/v1/presence/line")),
};
